import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";

interface AIToolsProps {
  title: string;
  description?: string;
  content?: string;
  galleryType?: string;
  venue?: string;
  onResult: (type: string, result: string) => void;
}

export const AIContentTools = ({
  title,
  description,
  content,
  galleryType,
  venue,
  onResult,
}: AIToolsProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const callAI = async (type: string) => {
    if (!title && type !== "blog-rewrite") {
      toast({ title: "Please enter a title first", variant: "destructive" });
      return;
    }

    setLoading(type);
    try {
      const { data, error } = await supabase.functions.invoke("ai-content", {
        body: { type, title, description, content, galleryType, venue },
      });

      if (error) throw error;
      
      onResult(type, data.result);
      toast({ title: "AI content generated!" });
    } catch (error: any) {
      console.error("AI error:", error);
      toast({
        title: "AI generation failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("blog-draft")}
        disabled={loading !== null}
      >
        {loading === "blog-draft" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Draft
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("seo-meta")}
        disabled={loading !== null}
      >
        {loading === "seo-meta" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI SEO
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("suggest-tags")}
        disabled={loading !== null}
      >
        {loading === "suggest-tags" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Tags
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("generate-slug")}
        disabled={loading !== null}
      >
        {loading === "generate-slug" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Slug
      </Button>
    </div>
  );
};

export const AIGalleryTools = ({
  title,
  description,
  galleryType,
  venue,
  onResult,
}: AIToolsProps) => {
  const [loading, setLoading] = useState<string | null>(null);
  const { toast } = useToast();

  const callAI = async (type: string) => {
    if (!title) {
      toast({ title: "Please enter a title first", variant: "destructive" });
      return;
    }

    setLoading(type);
    try {
      const { data, error } = await supabase.functions.invoke("ai-content", {
        body: { type, title, description, galleryType, venue },
      });

      if (error) throw error;
      
      onResult(type, data.result);
      toast({ title: "AI content generated!" });
    } catch (error: any) {
      toast({
        title: "AI generation failed",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("gallery-description")}
        disabled={loading !== null}
      >
        {loading === "gallery-description" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Description
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("seo-meta")}
        disabled={loading !== null}
      >
        {loading === "seo-meta" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI SEO
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("suggest-tags")}
        disabled={loading !== null}
      >
        {loading === "suggest-tags" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Tags
      </Button>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => callAI("generate-slug")}
        disabled={loading !== null}
      >
        {loading === "generate-slug" ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        AI Slug
      </Button>
    </div>
  );
};
